import React from 'react'
import { graphql } from 'gatsby'
import { css } from 'linaria'
import cx from 'classnames'
import PageWrap from '../components/Layouts/PageWrap'
import InsightsHeader from '../components/Headers/InsightsHeader'
import ImageRounded from '../components/Images/ImageRounded'
import { getImageBySize } from '../components/Images/Image'
import Avatar from '../components/Avatar'
import CardGroup from '../components/Cards/CardGroup'
import BlockRenderer from '../components/BlockRenderer'

// Page End Styling
const pageEnd = css`
  &:before {
    content: '';
    @apply block w-48 h-2 bg-blue-200 mx-auto mb-24;
  }
`

// Header
const transformHeaderData = data => {
  return {
    avatar: {
      name: data.author.node.name,
      role: data.author.node.acfUser.role,
      image: data.author.node.acfUser.profileImage
        ? getImageBySize(data.author.node.acfUser.profileImage, 'square-small')
        : 'https://placehold.it/150x150',
    },
    heading: data.title,
    labels: data.categories.nodes.map(category => ({
      url: category.uri,
      label: category.name,
    })),
    publishedDate:
      data.date !== data.modified ? `Updated: ${data.modified}` : data.date,
    publishedDateTime:
      data.date !== data.modified ? data.modifiedDateTime : data.dateTime,
  }
}

// Author Only
const transformAuthorData = data => {
  return {
    name: data.node.name,
    role: data.node.acfUser.role,
    bio: data.node.description,
    image: data.node.acfUser.profileImage
      ? getImageBySize(data.node.acfUser.profileImage, 'square-small')
      : 'https://placehold.it/150x150',
  }
}

// Header Image
const transformImageData = data => {
  if (!data) return
  return getImageBySize(data.node, 'post-feature')
}

// Related Content Image
const transformRelatedContentData = data => {
  const relatedContentCards = data.map(card => ({
    labels: card.categories.nodes.map(category => ({
      url: category.uri,
      label: category.name,
    })),
    heading: card.title,
    subheading: card.excerpt,
    author: {
      name: card.author.node.name,
      role: card.author.node.acfUser.role,
      image: card.author.node.acfUser.profileImage
        ? getImageBySize(card.author.node.acfUser.profileImage, 'square-small')
        : null,
    },
    link: {
      url: card.uri,
    },
  }))

  return {
    sectionHeader: {
      heading: 'More Insights',
      linkWithIcon: {
        link: {
          label: 'See All Insights',
          url: '/insights',
        },
      },
    },
    cards: relatedContentCards,
  }
}

const getRelatedContentData = pageData => {
  const { relatedContent } = pageData.acfInsight
  const relatedContentInCategory = pageData.categories.nodes[0]?.posts.nodes
    .slice(0, 2)
    .filter(post => post.id !== pageData.id)

  // If related content posts are set, use them
  if (relatedContent) {
    return transformRelatedContentData(relatedContent)
  }

  // Else, use the latest two posts in the same category
  if (relatedContentInCategory && relatedContentInCategory.length > 0) {
    return transformRelatedContentData(relatedContentInCategory)
  }

  return null
}

const Post = ({ data }) => {
  const relatedContentData = getRelatedContentData(data.page)
  const featuredImageData = transformImageData(data.page.featuredImage)

  // This is a work around to address the fact that some Yoast values
  // don't seem to be getting set correctly in the graphql data.
  if (data?.page?.seo) {
    data.page.seo.schema.pageType.push('Article')
  }

  return (
    <PageWrap seo={data?.page?.seo}>
      <div className="mb-20">
        <div className="container max-w-screen-md mb-12">
          <InsightsHeader {...transformHeaderData(data.page)} />
        </div>
        {featuredImageData && (
          <div className={cx('flex my-12 lg:mt-12 lg:mb-20 justify-end pl-5')}>
            <ImageRounded {...featuredImageData} />
          </div>
        )}
        <div className="container max-w-screen-md">
          <BlockRenderer blocks={data.page.blocks} />
          <div className={`${pageEnd} mt-32`}>
            <Avatar {...transformAuthorData(data.page.author)} />
          </div>
        </div>
      </div>
      {relatedContentData && (
        <div className="container my-40">
          <CardGroup {...relatedContentData} />
        </div>
      )}
    </PageWrap>
  )
}

export default Post

export const query = graphql`
  query($id: String!) {
    site: wp {
      seo {
        schema {
          siteUrl
        }
      }
    }
    page: wpPost(id: { eq: $id }) {
      seo {
        ...seoPostTypeFields
      }
      id
      title
      date(formatString: "MMMM D, Y")
      modified(formatString: "MMMM D, Y")
      dateTime: date(formatString: "Y-MM-DD")
      modifiedDateTime: modified(formatString: "Y-MM-DD")
      categories {
        nodes {
          uri
          name
          posts {
            nodes {
              ...postCardFields
            }
          }
        }
      }
      author {
        node {
          name
          description
          acfUser {
            role
            profileImage {
              ...mediaItemFields
            }
          }
        }
      }
      featuredImage {
        node {
          ...mediaItemFields
        }
      }
      blocks {
        ...blockFields
      }
      acfInsight {
        relatedContent {
          ... on WpPost {
            ...postCardFields
          }
        }
      }
    }
  }
`
